<template>
  <div class="layout-wrapper">
    <!-- Header / Navigation Bar -->
    <div class="menu-bar flex justify-content-between">
      <div class="home-button-wrapper">
        <Button icon="pi pi-fw pi-home" @click="goHome(slideContent)" aria-label="Go to home page" title="Home" />
        <span class="menu-title">Urenco Isotopes</span>
      </div>
      <Transition name="fade">
          <div v-if="currentPageIndex >= 0" class="iconNav gap-1">
            <div
                v-for="item in iconMenu"
                :key="item.id"
                class="icon-item"
            >
                <a :href="`/pages/${item?.slug}`" @click.prevent="goToPage(item?.slug, slideContent)" class="iconNavLink p-2 flex flex-column align-items-center justify-content-center"
                  :class="{'active': route.params.slug === item.slug}"
                  :title="item?.title || 'Navigate to page'">
                  <img v-if="item.iconImage && item.iconImage.url"
                       :src="resolveImageUrl(item.iconImage)"
                       :alt="item?.title || 'Untitled'"
                       class="icon-image">
                </a>
            </div>
          </div>
      </Transition>
      <Transition name="fade">
          <div v-if="!isHome" class="menu-navigation">
            <div v-if="pagesList && pagesList.length > 0" class="flex justify-content-center">
              <Button 
                icon="pi pi-chevron-left" 
                text 
                @click="goToPreviousPageOrHome(slideContent)" 
                :disabled="currentPageIndex <= 0" 
                aria-label="Go to previous page"
                title="Previous page"
              />
              <span class="mx-2 currentPageIndex">{{ currentPageIndex + 1 }}</span>
              <Button 
                :icon="currentPageIndex >= pagesList.length - 1 ? 'pi pi-home' : 'pi pi-chevron-right'" 
                @click="currentPageIndex >= pagesList.length - 1 ? goHome(slideContent) : goToNextPage(slideContent)"
                :aria-label="currentPageIndex >= pagesList.length - 1 ? 'Go to home page' : 'Go to next page'"
                :title="currentPageIndex >= pagesList.length - 1 ? 'Home' : 'Next page'"
              />
            </div>
          </div>
      </Transition>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="flex justify-content-center align-items-center" style="height: 70vh;">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5" />
    </div>

    <!-- Page Content -->
    <div 
      ref="slideContent"
      v-else 
      class="layout-content"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <slot />
    </div>

    <!-- Footer -->
    <div class="layout-footer">
      <div v-if="!isHome" class="menu-navigation-mobile">
        <div v-if="pagesList && pagesList.length > 0" class="flex justify-content-center">
          <Button 
            icon="pi pi-chevron-left" 
            text 
            @click="goToPreviousPageOrHome(slideContent)" 
            :disabled="currentPageIndex <= 0"
            aria-label="Go to previous page"
            title="Previous page"
          />
          <span class="mx-2 currentPageIndex">{{ currentPageIndex + 1 }}</span>
          <Button 
            :icon="currentPageIndex >= pagesList.length - 1 ? 'pi pi-home' : 'pi pi-chevron-right'" 
            @click="currentPageIndex >= pagesList.length - 1 ? goHome(slideContent) : goToNextPage(slideContent)"
            :aria-label="currentPageIndex >= pagesList.length - 1 ? 'Go to home page' : 'Go to next page'"
            :title="currentPageIndex >= pagesList.length - 1 ? 'Home' : 'Next page'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, useTemplateRef, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { useNavigation } from '../composables/useNavigation';
import { useMediaHelpers } from '../composables/useMediaHelpers.js';

const route = useRoute();
let isHome = computed(() => route.path === '/');
const { page } = defineProps('page')
const slideContent = ref(null)
const touchStartX = ref(0)
const touchEndX = ref(0)
const minSwipeDistance = 50 // Minimum distance in pixels to consider it a swipe
const { resolveImageUrl } = useMediaHelpers()

const {
    // State
    pagesList,
    iconMenu,
    currentPageIndex,
    loading,

    // Methods
    goToNextPage,
    goToPreviousPageOrHome,
    goHome,
    goToPage,
    hasNextPage,
    hasPreviousPage
} = useNavigation();

const handleTouchStart = (e) => {
  touchStartX.value = e.changedTouches[0].screenX
  touchEndX.value = touchStartX.value
}

const handleTouchMove = (e) => {
  touchEndX.value = e.changedTouches[0].screenX
}

const handleTouchEnd = () => {
  const distance = touchEndX.value - touchStartX.value
  
  // Only trigger navigation if swipe distance is greater than threshold
  if (Math.abs(distance) > minSwipeDistance) {
    if (distance < 0 && hasNextPage()) {
      // Swipe left - go to next page
      goToNextPage()
    } else if (distance > 0 && hasPreviousPage()) {
      // Swipe right - go to previous page or home
      goToPreviousPageOrHome()
    }
  }
}

// Add passive: false to prevent default behavior
const setupTouchListeners = () => {
  if (slideContent.value) {
    slideContent.value.addEventListener('touchmove', preventDefault, { passive: false })
  }
}

const cleanupTouchListeners = () => {
  if (slideContent.value) {
    slideContent.value.removeEventListener('touchmove', preventDefault)
  }
}

const preventDefault = (e) => {
  // Only prevent default if we're actually handling a swipe
  if (Math.abs(touchEndX.value - touchStartX.value) > 10) {
    e.preventDefault()
  }
}

onMounted(() => {
  setupTouchListeners()
})

onUnmounted(() => {
  cleanupTouchListeners()
})
</script>